/**
 * useRitualEngineStore — Configurable Ritual Engine
 *
 * Replaces the hardcoded Morning Manifesto / Evening Shutdown with a flexible
 * system supporting 3 ritual types: checkbox, ceremony, and timed.
 *
 * Entities:
 *   RitualDefinition  — what the ritual IS (template or user-created)
 *   RitualLog          — a single completion record
 *
 * Storage keys:
 *   'ritual_definitions' (collection) — user's ritual definitions
 *   'ritual_logs'        (collection) — completion history
 *
 * @see docs/data-schemas.md for full schema
 * @see src/constants/ritualTemplates.js for built-in templates
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import { localToday, localDateStr } from '../utils/dateUtils';

const DEFINITIONS_KEY = 'ritual_definitions';
const LOGS_KEY = 'ritual_logs';

// ─── XP Constants ───
const BASE_XP = {
  checkbox: 10,
  ceremony: 20,
  timed: 15,
};
const STREAK_BONUS_THRESHOLD = 7; // days
const STREAK_BONUS_XP = 5;        // extra XP per completion after 7-day streak
const BOTH_RITUALS_BONUS = 15;     // bonus for completing morning + evening same day

// ─── Streak Freeze Defaults ───
const MAX_FREEZES = 3;
const FREEZE_COST_COINS = 50;

const useRitualEngineStore = create((set, get) => ({
  // ─── State ───
  definitions: storage.get(DEFINITIONS_KEY, []),
  logs: storage.get(LOGS_KEY, []),

  _persistDefinitions() {
    storage.set(DEFINITIONS_KEY, get().definitions);
  },

  _persistLogs() {
    storage.set(LOGS_KEY, get().logs);
  },

  // ═══════════════════════════════════════════════════════
  // DEFINITION CRUD
  // ═══════════════════════════════════════════════════════

  /**
   * Add a new ritual definition (from template or custom).
   */
  addDefinition(def) {
    const now = new Date().toISOString();
    const newDef = {
      id: def.id || crypto.randomUUID(),
      name: def.name || 'New Ritual',
      emoji: def.emoji || '✨',
      type: def.type || 'checkbox',
      description: def.description || '',
      frequency: def.frequency || {
        type: 'daily',
        timeOfDay: 'any',
        scheduledTime: null,
        daysOfWeek: null,
        eventType: null,
      },
      trigger: def.trigger || 'manual',
      eventTrigger: def.eventTrigger || null,
      steps: def.steps || [],
      items: def.items || [],
      xpReward: def.xpReward ?? BASE_XP[def.type] ?? 10,
      isTemplate: false,
      templateId: def.templateId || null,
      areaId: def.areaId || null,
      archived: false,
      // Streak freeze state (per-ritual)
      streakFreezes: {
        available: 1, // start with 1 free freeze
        usedDates: [],
      },
      createdAt: def.createdAt || now,
      updatedAt: now,
    };

    set(state => ({
      definitions: [...state.definitions, newDef],
    }));
    get()._persistDefinitions();
    return newDef;
  },

  /**
   * Update an existing ritual definition.
   */
  updateDefinition(id, updates) {
    set(state => ({
      definitions: state.definitions.map(d =>
        d.id === id
          ? { ...d, ...updates, updatedAt: new Date().toISOString() }
          : d
      ),
    }));
    get()._persistDefinitions();
  },

  /**
   * Delete a ritual definition and all its logs.
   */
  deleteDefinition(id) {
    set(state => ({
      definitions: state.definitions.filter(d => d.id !== id),
      logs: state.logs.filter(l => l.ritualId !== id),
    }));
    get()._persistDefinitions();
    get()._persistLogs();
  },

  /**
   * Archive/unarchive a definition (soft delete).
   */
  archiveDefinition(id, archived = true) {
    get().updateDefinition(id, { archived });
  },

  /**
   * Duplicate (clone) an existing ritual definition.
   * Assigns new UUIDs to the definition, items, and steps.
   */
  duplicateDefinition(id) {
    const original = get().definitions.find(d => d.id === id);
    if (!original) return null;
    const now = new Date().toISOString();
    const clone = {
      ...original,
      id: crypto.randomUUID(),
      name: `${original.name} (copy)`,
      items: (original.items || []).map(item => ({ ...item, id: crypto.randomUUID() })),
      steps: (original.steps || []).map(step => ({ ...step, id: crypto.randomUUID() })),
      templateId: original.templateId || original.id,
      archived: false,
      streakFreezes: { available: 1, usedDates: [] },
      createdAt: now,
      updatedAt: now,
    };
    set(state => ({
      definitions: [...state.definitions, clone],
    }));
    get()._persistDefinitions();
    return clone;
  },

  // ═══════════════════════════════════════════════════════
  // RITUAL COMPLETION (LOGGING)
  // ═══════════════════════════════════════════════════════

  /**
   * Log a ritual completion.
   * @param {object} log - { ritualId, responses?, checkedItems? }
   * @returns {object} The created log entry with XP earned
   */
  completeRitual(log) {
    const def = get().definitions.find(d => d.id === log.ritualId);
    if (!def) return null;

    const today = localToday();

    // Don't allow double-completion for the same day
    const existingToday = get().logs.find(
      l => l.ritualId === log.ritualId && l.date === today
    );
    if (existingToday) {
      // If the existing log has xpEarned === 0 (e.g. partial checkbox log),
      // compute and apply XP now that the ritual is fully complete
      if (existingToday.xpEarned === 0) {
        let xpEarned = def.xpReward || BASE_XP[def.type] || 10;

        // Streak bonus
        const streak = get().getRitualStreak(def.id);
        if (streak >= STREAK_BONUS_THRESHOLD) {
          xpEarned += STREAK_BONUS_XP;
        }

        // Both-rituals-in-one-day bonus (morning + evening)
        const todayLogs = get().logs.filter(l => l.date === today && l.id !== existingToday.id);
        const todayDefs = todayLogs.map(l =>
          get().definitions.find(d => d.id === l.ritualId)
        ).filter(Boolean);
        const hasMorning = todayDefs.some(d => d.frequency?.timeOfDay === 'morning');
        const hasEvening = todayDefs.some(d => d.frequency?.timeOfDay === 'evening');
        const thisIsMorning = def.frequency?.timeOfDay === 'morning';
        const thisIsEvening = def.frequency?.timeOfDay === 'evening';
        if ((thisIsMorning && hasEvening) || (thisIsEvening && hasMorning)) {
          xpEarned += BOTH_RITUALS_BONUS;
        }

        const updatedLog = { ...existingToday, xpEarned, completedAt: new Date().toISOString() };
        set(state => ({
          logs: state.logs.map(l => l.id === existingToday.id ? updatedLog : l),
        }));
        get()._persistLogs();
        return updatedLog;
      }
      return existingToday;
    }

    // Calculate XP
    let xpEarned = def.xpReward || BASE_XP[def.type] || 10;

    // Streak bonus
    const streak = get().getRitualStreak(def.id);
    if (streak >= STREAK_BONUS_THRESHOLD) {
      xpEarned += STREAK_BONUS_XP;
    }

    // Both-rituals-in-one-day bonus (morning + evening)
    const todayLogs = get().logs.filter(l => l.date === today);
    const todayDefs = todayLogs.map(l =>
      get().definitions.find(d => d.id === l.ritualId)
    ).filter(Boolean);
    const hasMorning = todayDefs.some(d => d.frequency?.timeOfDay === 'morning');
    const hasEvening = todayDefs.some(d => d.frequency?.timeOfDay === 'evening');
    const thisIsMorning = def.frequency?.timeOfDay === 'morning';
    const thisIsEvening = def.frequency?.timeOfDay === 'evening';
    if ((thisIsMorning && hasEvening) || (thisIsEvening && hasMorning)) {
      xpEarned += BOTH_RITUALS_BONUS;
    }

    const entry = {
      id: crypto.randomUUID(),
      ritualId: log.ritualId,
      date: log.date || today,
      completedAt: new Date().toISOString(),
      responses: log.responses || {},
      checkedItems: log.checkedItems || [],
      xpEarned,
    };

    set(state => ({
      logs: [...state.logs, entry],
    }));
    get()._persistLogs();

    return entry;
  },

  /**
   * Uncomplete a ritual for today (undo).
   */
  uncompleteRitual(ritualId, date) {
    const targetDate = date || localToday();
    set(state => ({
      logs: state.logs.filter(
        l => !(l.ritualId === ritualId && l.date === targetDate)
      ),
    }));
    get()._persistLogs();
  },

  // ═══════════════════════════════════════════════════════
  // CHECKBOX SPECIFIC
  // ═══════════════════════════════════════════════════════

  /**
   * Toggle a single checkbox item for today.
   * Creates a partial log or updates the existing one.
   */
  toggleCheckboxItem(ritualId, itemId) {
    const today = localToday();
    const existing = get().logs.find(
      l => l.ritualId === ritualId && l.date === today
    );

    if (existing) {
      // Toggle item in existing log
      const checked = existing.checkedItems || [];
      const newChecked = checked.includes(itemId)
        ? checked.filter(id => id !== itemId)
        : [...checked, itemId];

      set(state => ({
        logs: state.logs.map(l =>
          l.id === existing.id
            ? { ...l, checkedItems: newChecked, completedAt: new Date().toISOString() }
            : l
        ),
      }));
      get()._persistLogs();
    } else {
      // Create new log with this item checked
      const entry = {
        id: crypto.randomUUID(),
        ritualId,
        date: today,
        completedAt: new Date().toISOString(),
        responses: {},
        checkedItems: [itemId],
        xpEarned: 0, // XP awarded on full completion only
      };
      set(state => ({ logs: [...state.logs, entry] }));
      get()._persistLogs();
    }
  },

  /**
   * Check if all items in a checkbox ritual are completed for a given date.
   */
  isCheckboxComplete(ritualId, date) {
    const targetDate = date || localToday();
    const def = get().definitions.find(d => d.id === ritualId);
    const log = get().logs.find(
      l => l.ritualId === ritualId && l.date === targetDate
    );
    if (!def || !log) return false;
    return def.items.length > 0 && (log.checkedItems || []).length >= def.items.length;
  },

  /**
   * Get checked items for a ritual on a specific date.
   */
  getCheckedItems(ritualId, date) {
    const targetDate = date || localToday();
    const log = get().logs.find(
      l => l.ritualId === ritualId && l.date === targetDate
    );
    return log?.checkedItems || [];
  },

  // ═══════════════════════════════════════════════════════
  // QUERIES
  // ═══════════════════════════════════════════════════════

  /**
   * Get all active (non-archived) definitions.
   */
  getActiveDefinitions() {
    return get().definitions.filter(d => !d.archived);
  },

  /**
   * Get definitions filtered by type.
   */
  getDefinitionsByType(type) {
    return get().getActiveDefinitions().filter(d => d.type === type);
  },

  /**
   * Get a single definition by ID.
   */
  getDefinition(id) {
    return get().definitions.find(d => d.id === id) || null;
  },

  /**
   * Check if a ritual has been completed today.
   */
  isCompletedToday(ritualId) {
    const today = localToday();
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return false;

    if (def.type === 'checkbox') {
      return get().isCheckboxComplete(ritualId, today);
    }

    return get().logs.some(
      l => l.ritualId === ritualId && l.date === today
    );
  },

  /**
   * Get today's completion status for all active rituals.
   * Returns [{ definition, log, completed, progress }]
   */
  getTodayStatus() {
    const today = localToday();
    const defs = get().getActiveDefinitions();

    return defs.map(def => {
      const log = get().logs.find(
        l => l.ritualId === def.id && l.date === today
      );

      let completed = false;
      let progress = 0;

      if (def.type === 'checkbox') {
        const checkedCount = (log?.checkedItems || []).length;
        const totalItems = def.items.length;
        progress = totalItems > 0 ? checkedCount / totalItems : 0;
        completed = progress >= 1;
      } else {
        completed = !!log;
        progress = completed ? 1 : 0;
      }

      return { definition: def, log, completed, progress };
    });
  },

  /**
   * Get rituals that should be shown right now based on time of day.
   */
  getRitualsForTimeOfDay() {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : 'evening';
    return get().getActiveDefinitions().filter(d => {
      const tod = d.frequency?.timeOfDay;
      return tod === 'any' || tod === timeOfDay || !tod;
    });
  },

  /**
   * Get logs for a specific date range (for heatmap).
   */
  getLogsByDateRange(startDate, endDate) {
    return get().logs.filter(
      l => l.date >= startDate && l.date <= endDate
    );
  },

  /**
   * Get all completion dates for a ritual (for heatmap).
   * Returns a Map<dateString, { completed: boolean, checkedCount: number }>
   */
  getCompletionMap(ritualId, days = 90) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return new Map();

    const map = new Map();
    const now = new Date();

    for (let i = 0; i < days; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = localDateStr(d);

      const log = get().logs.find(
        l => l.ritualId === ritualId && l.date === dateStr
      );

      if (def.type === 'checkbox') {
        const checked = (log?.checkedItems || []).length;
        const total = def.items.length;
        map.set(dateStr, {
          completed: total > 0 && checked >= total,
          checkedCount: checked,
          totalItems: total,
          progress: total > 0 ? checked / total : 0,
        });
      } else {
        map.set(dateStr, {
          completed: !!log,
          checkedCount: log ? 1 : 0,
          totalItems: 1,
          progress: log ? 1 : 0,
        });
      }
    }

    return map;
  },

  // ═══════════════════════════════════════════════════════
  // STREAKS
  // ═══════════════════════════════════════════════════════

  /**
   * Calculate streak for a specific ritual.
   * A streak counts consecutive days the ritual was completed.
   */
  getRitualStreak(ritualId) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return 0;

    // Get all dates this ritual was completed (deduplicated, sorted desc)
    let completedDates;
    if (def.type === 'checkbox') {
      // Only count days where ALL items were checked
      completedDates = [...new Set(
        get().logs
          .filter(l => l.ritualId === ritualId)
          .filter(l => {
            const checked = (l.checkedItems || []).length;
            return def.items.length > 0 && checked >= def.items.length;
          })
          .map(l => l.date)
      )].sort().reverse();
    } else {
      completedDates = [...new Set(
        get().logs.filter(l => l.ritualId === ritualId).map(l => l.date)
      )].sort().reverse();
    }

    if (completedDates.length === 0) return 0;

    const today = localToday();
    const yesterday = localDateStr(new Date(Date.now() - 86400000));

    // Include freeze dates as "completed" for streak continuity
    const freezeDates = new Set(def.streakFreezes?.usedDates || []);

    // Streak must include today or yesterday (or a freeze covers the gap)
    if (completedDates[0] !== today && completedDates[0] !== yesterday) {
      // Check if freeze covers the gap
      const daysSinceLastCompletion = Math.round(
        (new Date(today) - new Date(completedDates[0])) / 86400000
      );
      let freezeCoversGap = true;
      for (let i = 1; i < daysSinceLastCompletion; i++) {
        const checkDate = localDateStr(new Date(Date.now() - i * 86400000));
        if (!freezeDates.has(checkDate) && !completedDates.includes(checkDate)) {
          freezeCoversGap = false;
          break;
        }
      }
      if (!freezeCoversGap) return 0;
    }

    // Count consecutive days (including freeze dates)
    let streak = 1;
    const allDates = [...new Set([...completedDates, ...freezeDates])].sort().reverse();

    for (let i = 0; i < allDates.length - 1; i++) {
      const current = new Date(allDates[i] + 'T00:00:00');
      const prev = new Date(allDates[i + 1] + 'T00:00:00');
      const diff = Math.round((current - prev) / 86400000);
      if (diff === 1) streak++;
      else break;
    }

    return streak;
  },

  /**
   * Get the longest streak ever achieved for a ritual.
   */
  getLongestStreak(ritualId) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return 0;

    const dates = [...new Set(
      get().logs.filter(l => l.ritualId === ritualId).map(l => l.date)
    )].sort();

    if (dates.length === 0) return 0;

    let longest = 1;
    let current = 1;

    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1] + 'T00:00:00');
      const curr = new Date(dates[i] + 'T00:00:00');
      const diff = Math.round((curr - prev) / 86400000);
      if (diff === 1) {
        current++;
        longest = Math.max(longest, current);
      } else {
        current = 1;
      }
    }

    return longest;
  },

  /**
   * Get overall completion rate for a ritual (last N days).
   */
  getCompletionRate(ritualId, days = 30) {
    const completionMap = get().getCompletionMap(ritualId, days);
    let completedDays = 0;
    completionMap.forEach(v => { if (v.completed) completedDays++; });
    return days > 0 ? completedDays / days : 0;
  },

  // ═══════════════════════════════════════════════════════
  // STREAK FREEZES
  // ═══════════════════════════════════════════════════════

  /**
   * Use a streak freeze to protect a missed day.
   * @param {string} ritualId
   * @param {string} date — YYYY-MM-DD to freeze (typically yesterday)
   * @returns {boolean} success
   */
  useStreakFreeze(ritualId, date) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return false;

    const freezes = def.streakFreezes || { available: 0, usedDates: [] };
    if (freezes.available <= 0) return false;

    // Already used for this date
    if (freezes.usedDates.includes(date)) return false;

    get().updateDefinition(ritualId, {
      streakFreezes: {
        available: freezes.available - 1,
        usedDates: [...freezes.usedDates, date],
      },
    });

    return true;
  },

  /**
   * Purchase a streak freeze with coins.
   * Calls gamification store externally.
   * @returns {boolean} success
   */
  addStreakFreeze(ritualId) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return false;

    const freezes = def.streakFreezes || { available: 0, usedDates: [] };
    if (freezes.available >= MAX_FREEZES) return false;

    get().updateDefinition(ritualId, {
      streakFreezes: {
        ...freezes,
        available: freezes.available + 1,
      },
    });

    return true;
  },

  /**
   * Auto-freeze yesterday if the user missed it and has freezes available.
   * Called on app startup / when ritual hub loads.
   */
  autoFreezeYesterday(ritualId) {
    const def = get().definitions.find(d => d.id === ritualId);
    if (!def) return false;

    const yesterday = localDateStr(new Date(Date.now() - 86400000));

    // Was yesterday completed?
    const yesterdayLog = get().logs.find(
      l => l.ritualId === ritualId && l.date === yesterday
    );
    if (yesterdayLog) return false; // Already completed, no freeze needed

    // Does user have a streak worth protecting?
    const currentStreak = get().getRitualStreak(ritualId);
    if (currentStreak < 3) return false; // Don't freeze trivial streaks

    return get().useStreakFreeze(ritualId, yesterday);
  },

  // ═══════════════════════════════════════════════════════
  // IMPORT / EXPORT
  // ═══════════════════════════════════════════════════════

  /**
   * Export a single definition as a portable JSON object.
   * Strips runtime state (logs, freezes) so it can be shared.
   */
  exportDefinition(id) {
    const def = get().definitions.find(d => d.id === id);
    if (!def) return null;

    const exportObj = {
      _format: 'alphafocus-ritual',
      _version: 1,
      _exportedAt: new Date().toISOString(),
      definitions: [{
        name: def.name,
        emoji: def.emoji,
        type: def.type,
        description: def.description,
        frequency: def.frequency,
        trigger: def.trigger,
        eventTrigger: def.eventTrigger,
        items: (def.items || []).map(({ label, emoji, order }) => ({ label, emoji, order })),
        steps: (def.steps || []).map(({ type, label, description, options, duration, required, order }) =>
          ({ type, label, description, options, duration, required, order })
        ),
        xpReward: def.xpReward,
        areaId: null, // Don't export area binding
      }],
    };

    return JSON.stringify(exportObj, null, 2);
  },

  /**
   * Export all active (non-archived) definitions.
   */
  exportAllDefinitions() {
    const defs = get().getActiveDefinitions();
    const exportObj = {
      _format: 'alphafocus-ritual',
      _version: 1,
      _exportedAt: new Date().toISOString(),
      definitions: defs.map(def => ({
        name: def.name,
        emoji: def.emoji,
        type: def.type,
        description: def.description,
        frequency: def.frequency,
        trigger: def.trigger,
        eventTrigger: def.eventTrigger,
        items: (def.items || []).map(({ label, emoji, order }) => ({ label, emoji, order })),
        steps: (def.steps || []).map(({ type, label, description, options, duration, required, order }) =>
          ({ type, label, description, options, duration, required, order })
        ),
        xpReward: def.xpReward,
        areaId: null,
      })),
    };

    return JSON.stringify(exportObj, null, 2);
  },

  /**
   * Import ritual definitions from a JSON string.
   * Validates format and assigns new UUIDs.
   * @param {string} jsonString
   * @returns {{ imported: number, errors: string[] }}
   */
  importDefinitions(jsonString) {
    const errors = [];
    let parsed;

    try {
      parsed = JSON.parse(jsonString);
    } catch {
      return { imported: 0, errors: ['Invalid JSON format'] };
    }

    if (parsed._format !== 'alphafocus-ritual') {
      return { imported: 0, errors: ['Not an Alphafocus ritual file'] };
    }

    if (!Array.isArray(parsed.definitions) || parsed.definitions.length === 0) {
      return { imported: 0, errors: ['No ritual definitions found in file'] };
    }

    const validTypes = ['checkbox', 'ceremony', 'timed'];
    let imported = 0;

    for (const raw of parsed.definitions) {
      if (!raw.name || !validTypes.includes(raw.type)) {
        errors.push(`Skipped "${raw.name || 'unnamed'}": invalid type`);
        continue;
      }

      const def = {
        name: raw.name,
        emoji: raw.emoji || '✨',
        type: raw.type,
        description: raw.description || '',
        frequency: raw.frequency || { type: 'daily', timeOfDay: 'any', scheduledTime: null, daysOfWeek: null },
        trigger: raw.trigger || 'manual',
        eventTrigger: raw.eventTrigger || null,
        items: (raw.items || []).map(item => ({ ...item, id: crypto.randomUUID() })),
        steps: (raw.steps || []).map(step => ({ ...step, id: crypto.randomUUID() })),
        xpReward: raw.xpReward ?? 10,
        templateId: null,
      };

      get().addDefinition(def);
      imported++;
    }

    return { imported, errors };
  },

  // ═══════════════════════════════════════════════════════
  // SUMMARY STATS
  // ═══════════════════════════════════════════════════════

  /**
   * Get a summary of today's ritual status.
   * Used by the Ritual Status Widget on Today page.
   */
  getTodaySummary() {
    const statuses = get().getTodayStatus();
    const total = statuses.length;
    const completed = statuses.filter(s => s.completed).length;
    const totalProgress = total > 0
      ? statuses.reduce((sum, s) => sum + s.progress, 0) / total
      : 0;

    return {
      total,
      completed,
      remaining: total - completed,
      progress: totalProgress,
      statuses,
    };
  },

  /**
   * Total XP earned from rituals (all time).
   */
  getTotalRitualXP() {
    return get().logs.reduce((sum, l) => sum + (l.xpEarned || 0), 0);
  },
}));

// ─── Rehydrate on cloud sync ───
onRehydrate(() => {
  useRitualEngineStore.setState({
    definitions: storage.get(DEFINITIONS_KEY, []),
    logs: storage.get(LOGS_KEY, []),
  });
});

export default useRitualEngineStore;
export { BASE_XP, STREAK_BONUS_THRESHOLD, STREAK_BONUS_XP, MAX_FREEZES, FREEZE_COST_COINS };
